// Pure NAV-anchor derivation, extracted from useSignalPipeline.ts:233-249
// (2026-09-06) so BGA / JH / gold can share the same anchor-selection rule
// without composable-level coupling.
//
// Display rule (agreed 2026-04-16, see useSignalPipeline.ts):
//   anchor = today's BG official NAV when published, else prev official NAV
// Both inputs arrive in POUNDS (`*_official_nav_gbp` field names); the
// output is in PENCE (x100) to match every downstream anchor consumer
// (anchorPence props, synthNavPence, the £/unit HoldingsPanel column, etc).
//
// A non-positive value (<= 0) is treated as absent, matching the inline
// `v != null && v > 0` guard this was extracted from — a NAV of exactly
// zero or negative is not a valid publish and must not become a live
// anchor kind.
//
// No Vue reactivity here by design: consumers wrap this in
// `computed(() => deriveNavAnchor(...))`.

export interface NavAnchorInput {
  todayOfficialNavGbp: number | null
  prevOfficialNavGbp: number | null
}

export interface NavAnchor {
  anchorPence: number | null
  anchorKind: 'today' | 'prev' | null
}

const GBP_TO_PENCE = 100

function toPenceIfValid(navGbp: number | null): number | null {
  return navGbp != null && navGbp > 0 ? navGbp * GBP_TO_PENCE : null
}

export function deriveNavAnchor(input: NavAnchorInput): NavAnchor {
  const todayPence = toPenceIfValid(input.todayOfficialNavGbp)
  if (todayPence != null) {
    return { anchorPence: todayPence, anchorKind: 'today' }
  }
  const prevPence = toPenceIfValid(input.prevOfficialNavGbp)
  if (prevPence != null) {
    return { anchorPence: prevPence, anchorKind: 'prev' }
  }
  return { anchorPence: null, anchorKind: null }
}
